/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  RiskExposureSlice,
  RiskExposureSlice$inboundSchema,
} from "./riskexposureslice.js";
import { RiskSignal, RiskSignal$inboundSchema } from "./risksignal.js";

export type RiskSignalsResult = {
  /**
   * Signals rated critical in the window.
   */
  criticalSignals: number;
  /**
   * Finding counts by category, largest first.
   */
  exposure: Array<RiskExposureSlice>;
  /**
   * Deduplicated live findings in the window.
   */
  findings: number;
  /**
   * Inclusive start of the signals window.
   */
  from: Date;
  /**
   * Signals with at least one live finding in the window.
   */
  openSignals: number;
  /**
   * Heuristic organization risk score on the 0.1-10 scale, blended from the top signal scores and finding volume. Zero when the window has no findings.
   */
  orgRiskScore: number;
  /**
   * Deduplicated live findings in the equal-length window immediately before from.
   */
  previousFindings: number;
  /**
   * Organization risk score computed the same way over the equal-length window immediately before from.
   */
  previousOrgRiskScore: number;
  /**
   * Distinct users with at least one finding in the equal-length window immediately before from.
   */
  previousUsersExposed: number;
  /**
   * Signals ranked by risk score, highest first.
   */
  signals: Array<RiskSignal>;
  /**
   * Exclusive end of the signals window.
   */
  to: Date;
  /**
   * Distinct users with at least one finding in the window.
   */
  usersExposed: number;
};

/** @internal */
export const RiskSignalsResult$inboundSchema: z.ZodMiniType<
  RiskSignalsResult,
  unknown
> = z.pipe(
  z.object({
    critical_signals: z.int(),
    exposure: z.array(RiskExposureSlice$inboundSchema),
    findings: z.int(),
    from: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    open_signals: z.int(),
    org_risk_score: z.number(),
    previous_findings: z.int(),
    previous_org_risk_score: z.number(),
    previous_users_exposed: z.int(),
    signals: z.array(RiskSignal$inboundSchema),
    to: z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    users_exposed: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "critical_signals": "criticalSignals",
      "open_signals": "openSignals",
      "org_risk_score": "orgRiskScore",
      "previous_findings": "previousFindings",
      "previous_org_risk_score": "previousOrgRiskScore",
      "previous_users_exposed": "previousUsersExposed",
      "users_exposed": "usersExposed",
    });
  }),
);

export function riskSignalsResultFromJSON(
  jsonString: string,
): SafeParseResult<RiskSignalsResult, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskSignalsResult$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskSignalsResult' from JSON`,
  );
}
